import { useEffect, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'agentica.theme';
const PAPER_CLASS = 'theme-paper';
const PAPER_LIGHT_CLASS = 'theme-paper-light';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

export type PaperMode = 'dark' | 'light';

/**
 * Resolved theme actually applied to the DOM. ``paper-auto`` is collapsed
 * here to either ``paper-dark`` or ``paper-light`` based on the OS
 * preference; consumers that just want to know "what surface am I on"
 * should read this.
 */
export type ThemeValue = 'paper-dark' | 'paper-light' | null;

/**
 * The user's stored choice, including ``paper-auto`` (follow the OS
 * preference). The settings UI renders this — the toggle should reflect
 * what the user picked, not what it currently resolves to.
 */
export type ThemePreference = 'paper-auto' | 'paper-dark' | 'paper-light' | null;

const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const emit = () => listeners.forEach((l) => l());

const systemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // Safe default for SSR / older browsers: dark, matching the historical
    // Agentica surface so we don't accidentally flash a cream paper.
    return true;
  }
  return window.matchMedia(SYSTEM_DARK_QUERY).matches;
};

const resolvePreference = (pref: ThemePreference): ThemeValue => {
  if (pref === 'paper-auto') return systemPrefersDark() ? 'paper-dark' : 'paper-light';
  return pref;
};

const normalizePreference = (raw: string | null): ThemePreference | undefined => {
  if (raw === 'paper-dark' || raw === 'paper') return 'paper-dark';
  if (raw === 'paper-light') return 'paper-light';
  if (raw === 'paper-auto' || raw === 'auto' || raw === 'system') return 'paper-auto';
  if (raw === 'legacy') return null;
  if (raw === null) return undefined;
  // Anything else stored explicitly is treated as auto so a stray value
  // doesn't quietly drop the user into the legacy UI.
  return 'paper-auto';
};

const readStored = (): ThemePreference => {
  if (typeof window === 'undefined') return 'paper-auto';
  const normalized = normalizePreference(window.localStorage.getItem(STORAGE_KEY));
  // Missing key → default to following the OS. Existing explicit picks
  // (including the legacy ``null``) are preserved.
  return normalized === undefined ? 'paper-auto' : normalized;
};

const readInitial = (): ThemePreference => {
  if (typeof window === 'undefined') return 'paper-auto';
  const url = new URL(window.location.href);
  const qp = url.searchParams.get('theme');
  if (qp === 'paper' || qp === 'paper-dark') {
    window.localStorage.setItem(STORAGE_KEY, 'paper-dark');
    return 'paper-dark';
  }
  if (qp === 'paper-light') {
    window.localStorage.setItem(STORAGE_KEY, 'paper-light');
    return 'paper-light';
  }
  if (qp === 'paper-auto' || qp === 'auto' || qp === 'system') {
    window.localStorage.removeItem(STORAGE_KEY);
    return 'paper-auto';
  }
  if (qp === 'default' || qp === 'off') {
    // Preserved escape hatch: opt out of the paper UI entirely. Stored
    // explicitly as the legacy ``null`` so a refresh keeps the user out
    // of paper without re-needing the URL param.
    window.localStorage.setItem(STORAGE_KEY, 'legacy');
    return null;
  }
  return readStored();
};

const applyClass = (resolved: ThemeValue) => {
  const root = document.documentElement;
  const paperOn = resolved === 'paper-dark' || resolved === 'paper-light';
  root.classList.toggle(PAPER_CLASS, paperOn);
  root.classList.toggle(PAPER_LIGHT_CLASS, resolved === 'paper-light');
};

export const getPaperThemePreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'paper-auto';
  const url = new URL(window.location.href);
  const qp = url.searchParams.get('theme');
  if (qp === 'paper' || qp === 'paper-dark') return 'paper-dark';
  if (qp === 'paper-light') return 'paper-light';
  if (qp === 'paper-auto' || qp === 'auto' || qp === 'system') return 'paper-auto';
  if (qp === 'default' || qp === 'off') return null;
  return readStored();
};

export const getPaperTheme = (): ThemeValue =>
  resolvePreference(getPaperThemePreference());

export const isPaperThemeActive = (): boolean => getPaperTheme() !== null;

export const setPaperTheme = (value: ThemePreference) => {
  if (typeof window === 'undefined') return;
  if (value === 'paper-auto') {
    // Auto is the implicit default; clearing the key keeps future visits
    // on the OS-following path without us having to write 'paper-auto'.
    window.localStorage.removeItem(STORAGE_KEY);
  } else if (value === null) {
    // Legacy opt-out: stored explicitly so a refresh stays out of paper.
    window.localStorage.setItem(STORAGE_KEY, 'legacy');
  } else {
    window.localStorage.setItem(STORAGE_KEY, value);
  }
  applyClass(resolvePreference(value));
  emit();
};

export const setPaperThemeActive = (active: boolean) => {
  setPaperTheme(active ? 'paper-auto' : null);
};

export const usePaperThemeActive = (): boolean =>
  useSyncExternalStore(subscribe, isPaperThemeActive, () => false);

export const usePaperTheme = (): ThemeValue =>
  useSyncExternalStore(subscribe, getPaperTheme, () => null);

export const usePaperThemePreference = (): ThemePreference =>
  useSyncExternalStore(subscribe, getPaperThemePreference, () => 'paper-auto');

export const useThemeFlag = () => {
  useEffect(() => {
    applyClass(resolvePreference(readInitial()));

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    // Re-apply the class and re-notify subscribers when the OS theme
    // changes — but only when the user is actually in ``paper-auto``.
    // Explicit dark/light picks should win against the OS, so a system
    // flip while the user is on ``paper-light`` must do nothing.
    const mql = window.matchMedia(SYSTEM_DARK_QUERY);
    const handler = () => {
      if (getPaperThemePreference() !== 'paper-auto') return;
      applyClass(resolvePreference('paper-auto'));
      emit();
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
};
